import React, { useState, useEffect, useCallback, useRef } from 'react';
import useAutosaveRascunho from '../../../utils/useAutosaveRascunho';
import { Send, Image as ImageIcon, X, BookOpen, AlertCircle, CheckCircle2, ListChecks, FileClock } from 'lucide-react';
import useTelaMobile from '../../../utils/useTelaMobile';
import EscolherAnexo from '../../EscolherAnexo';
import { filtrarAtivos, filtrarPorFamilia, filtrarPorQuiosque } from '../ativos/chamados-busca-ativos';
// ⚠️ TEMPORÁRIO ATÉ 18/09/2026: a janela em que a loja pode informar a data de
//    abertura, para transcrever os chamados que já estavam no Auvo. Espelho de
//    `server/chamados/chamados-migracao-auvo.js`, travado por teste.
import {
  FIM_JANELA, INICIO_PERMITIDO, dataLocalISO, formatarBR,
  janelaMigracaoAberta, resolverAberturaInformada, podeInformarAbertura,
} from './chamados-migracao-auvo';
// ⚠️ Traduz perfil derivado (`Diretoria Op` → `Diretoria`) e SEMPRE com o
//    módulo — o mesmo que a `ChamadosConsulta` faz.
import { perfilNoModulo } from '../../../utils/perfisDerivados';
// ⚠️ ESPELHO de `server/chamados/chamados-imagem.js` (travado por teste): o teto
//    de canais e a lista de destinos precisam valer nos dois lados.
import {
  DESTINOS_IMAGEM, ehSolicitacaoImagem, tetoDeCanais,
} from './chamados-imagem-utils';
// ⚠️ ESPELHO de `server/chamados/atendimento/chamados-chip.js` (travado por teste):
//    o tipo, os motivos e a regra da linha atual (DEM-00027).
import {
  ehChip as ehChipTipo, MOTIVOS_CHIP, motivoExigeLinha,
} from './chamados-chip-utils';

// =====================================================================
// Chamados — ABERTURA (ato 1 de 4).
//
// Quem abre: `Gerente` e `Gerente Plantao`, sempre para a PRÓPRIA loja.
// ⚠️ `Gerente Adm` fica FORA (decisão da Julia, 21/08/2026) — o backend recusa
//    igual, esta tela só não é oferecida a ele.
//
// ⚠️⚠️ NÃO EXISTE CAMPO DE PRIORIDADE AQUI, e é a decisão mais importante desta
//    tela. Quem define a prioridade é o Gerente TI, na triagem. No Auvo a loja
//    escolhia, e o resultado medido foi 81% "Alta" na fila de Manutenção — quem
//    sofre o problema não tem incentivo nenhum para escolher "Média". O backend
//    ignora prioridade vinda daqui.
//
// ⚠️ A BASE DE CONHECIMENTO VEM ANTES DO FORMULÁRIO, não numa aba ao lado
//    (decisão da Julia, 21/08/2026): autoatendimento que mora em outra tela não
//    é lido. O manual do Sebastião ainda não foi entregue — o bloco já nasce
//    posicionado, dizendo o que é, para a chegada do conteúdo ser só conteúdo.
// =====================================================================

const API = '/api/chamados';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const COR = 'var(--ch-cor)';
const COR_ESCURA = 'var(--ch-cor-escura)';
// ⚠️ As variantes translúcidas são variáveis PRÓPRIAS: `var(--ch-cor)22` é CSS
//    inválido — sufixo de alfa não se concatena a uma função. Ver o bloco
//    "MÓDULO CHAMADOS — A COR DA FILA" no index.css.
const COR_A22 = 'var(--ch-cor-aa22)';   // o mesmo matiz, translúcido
const COR_A33 = 'var(--ch-cor-aa33)';   // o mesmo matiz, translúcido
const COR_A55 = 'var(--ch-cor-aa55)';   // o mesmo matiz, translúcido
const COR_CLARA = 'var(--ch-cor-clara)';

/**
 * "12/08 às 14:30" — quando o rascunho foi salvo.
 * ⚠️ Sem o ano de propósito: rascunho é de horas atrás, e a data cheia só faria
 *    a frase mais longa sem dizer mais nada.
 */
const fmtQuando = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    }).replace(', ', ' às ');
};
// 1 obrigatória + 4 opcionais (decisão da Julia, 21/08/2026).
// ⚠️ O mínimo é conferido TAMBÉM no servidor — aqui é atalho, lá é regra.
const MAX_FOTOS = 5;
const MIN_FOTOS = 1;

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };

// A tabelinha de prazos do SLA, no fim do formulário.
const thPrazo = {
    textAlign: 'left', padding: '6px 12px 6px 0', fontSize: 11, fontWeight: 700,
    color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap',
};
const tdPrazo = {
    padding: '6px 12px 6px 0', borderBottom: '1px solid #f1f5f9',
    color: '#334155', whiteSpace: 'nowrap',
};

/**
 * `useEffect` que IGNORA a primeira renderização — só roda quando o valor MUDA
 * de verdade.
 *
 * ⚠️⚠️ Existe por causa de um bug medido em 11/09/2026: os dois efeitos que
 *    LIMPAM o equipamento (ao trocar de loja e ao trocar de lugar) também
 *    rodavam na montagem, e apagavam o equipamento que a etiqueta do QR Code
 *    tinha acabado de preencher. O sintoma era exatamente "o QR identificou a
 *    loja, mas não o equipamento": a loja sobrevivia porque nada a limpa, e o
 *    equipamento nascia certo e era zerado milissegundos depois.
 * ⚠️ Não dá para resolver "não limpando": a limpeza é o que impede o `CODIGO` de
 *    outra loja/outro lugar de continuar selecionado num formulário que parece
 *    correto (o POST recusaria). O que estava errado era o efeito rodar na
 *    montagem, quando NINGUÉM trocou nada.
 */
function useAoMudar(valor, fn) {
  const montou = useRef(false);
  useEffect(() => {
    if (!montou.current) { montou.current = true; return; }
    fn();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- de propósito: dispara pelo VALOR, não pela identidade da função
  }, [valor]);
}

// ⚠️⚠️ `equipe` É A FILA DO CHAMADO ('TI' | 'Manutencao'), e ela decide DUAS
//    coisas nesta tela: de qual catálogo vem o seletor de equipamento e o que vai
//    no `INSERT`. O padrão 'TI' mantém o comportamento de sempre para todo
//    caminho que ainda não passa a equipe.
const ChamadosAbertura = ({
  onIrParaConsulta, onIrParaManual, familiaFiltro = null, etiqueta = null, equipe = 'TI',
}) => {
  /** Esta tela está abrindo chamado para a Manutenção? */
  const ehManutencao = equipe === 'Manutencao';
  // ⚠️ Esta é a tela MAIS usada no celular do módulo: quem a abre é o gerente,
  //    de pé na loja, com o equipamento quebrado à frente (Julia, 24/08/2026).
  const { ehMobile } = useTelaMobile();

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [ativos, setAtivos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [tipoSolicitacao, setTipoSolicitacao] = useState('');

  // ---------------------------------------------------------------------
  // A SOLICITAÇÃO DE IMAGEM (Julia, 14/09/2026) — três campos que só existem
  // neste tipo: quais canais de câmera, o período e o destino (com o motivo).
  //
  // ⚠️⚠️ ESTE TIPO DISPENSA A FOTO OBRIGATÓRIA, e sem isso ele seria impossível
  //    de enviar: quem pede a gravação do CFTV não tem o que fotografar. O
  //    servidor dispensa pela mesma função — aqui é só o atalho.
  // ⚠️ O TETO DE CANAIS É DERIVADO DO QUIOSQUE (32 no restaurante, 4 no
  //    quiosque). Por isso trocar o quiosque LIMPA os canais já marcados: manter
  //    o canal 9 ao trocar para um quiosque de 4 deixaria na tela uma escolha
  //    que o servidor recusa — e o erro só apareceria ao enviar.
  const [canais, setCanais] = useState([]);
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [destinoImagem, setDestinoImagem] = useState('');
  const [motivoImagem, setMotivoImagem] = useState('');
  const ehImagem = ehSolicitacaoImagem(tipoSolicitacao);

  // ---------------------------------------------------------------------
  // A SOLICITAÇÃO DE CHIP (DEM-00027) — campos próprios, como no imagem.
  //
  // ⚠️ Pedido ADMINISTRATIVO: dispensa foto e equipamento e não passa por
  //    triagem — nasce direto com a Fernanda. O tipo só aparece no seletor para o
  //    Gerente titular (o servidor recorta a lista em `GET /tipos-solicitacao`).
  // ⚠️ 'Linha atual' só é obrigatória em troca/2ª via/cancelamento
  //    (`motivoExigeLinha`) — em 'chip novo' não há número atual.
  // ---------------------------------------------------------------------
  const [colaboradorChip, setColaboradorChip] = useState('');
  const [motivoChip, setMotivoChip] = useState('');
  const [linhaChip, setLinhaChip] = useState('');
  const [obsChip, setObsChip] = useState('');
  const ehChip = ehChipTipo(tipoSolicitacao);
  // ⚠️ NASCE PREENCHIDO quando a pessoa chegou pelo QR CODE do adesivo colado no
  //    equipamento (prop `etiqueta`, posta pelo hub — ver ChamadosEtiquetaEntrada).
  //    É o ganho inteiro do projeto de etiquetagem: 32% dos chamados de TI do Auvo
  //    não diziam qual aparelho era, porque o campo é opcional e escolher da lista
  //    no meio de um problema dá trabalho.
  const [idAtivo, setIdAtivo] = useState(etiqueta?.ativo?.codigo || '');

  // ---------------------------------------------------------------------
  // A BUSCA E O RECORTE DO SELETOR DE EQUIPAMENTO (2026-08-28)
  // ---------------------------------------------------------------------
  // São DUAS coisas diferentes empilhadas, e confundi-las quebra as duas:
  //   • `buscaAtivo`   — o que a PESSOA digitou. Some quando ela apaga.
  //   • `familiaFiltro`— o que a FICHA DO GUIA indicou (prop, vem do hub). Ela
  //                      não pediu, então precisa ser anunciado e ter saída.
  const [buscaAtivo, setBuscaAtivo] = useState('');
  const [ignorarFiltro, setIgnorarFiltro] = useState(false);

  // ---------------------------------------------------------------------
  // A LOJA DO CHAMADO (2026-08-28) — deixou de ser sempre a de quem abre.
  // ---------------------------------------------------------------------
  // ⚠️ Pedido da Julia: o `Gerente TI` e o `TI` atendem a rede e recebem o
  //    chamado POR TELEFONE — precisam registrar no nome da loja que ligou. Até
  //    aqui a loja era, sempre, a do cadastro de quem abre; como os quatro estão
  //    lotados no ESCRITÓRIO, todo chamado deles nasceria no escritório.
  //
  // ⚠️ QUEM DECIDE É O BACKEND, não esta tela: o `GET /lojas` devolve a lista já
  //    recortada (a rede para quem é da rede, a própria loja para o gerente).
  //    Se a lista vier com uma opção só, o seletor nem aparece — é o caso da
  //    loja, e para ela nada muda na tela.
  const [lojas, setLojas] = useState([]);
  // ⚠️ Vindo do adesivo, a loja é a DO EQUIPAMENTO, não a de quem escaneia: o
  //    técnico de TI está lotado no ESCRITÓRIO e pode escanear o POS de qualquer
  //    loja. Sem isto o chamado nasceria no escritório e o seletor carregaria o
  //    parque errado — deixando o equipamento da etiqueta fora da própria lista.
  const [lojaAlvo, setLojaAlvo] = useState(etiqueta?.ativo?.loja || user?.loja || '');
  const podeEscolherLoja = lojas.length > 1;

  // ⚠️ Trocar de ficha no guia REARMA o recorte: sem isto, quem clicou uma vez
  //    em "ver todos" nunca mais veria filtro nenhum nesta sessão de tela.
  useEffect(() => { setIgnorarFiltro(false); }, [familiaFiltro]);

  // ⚠️ Declarado aqui em cima porque o recorte por LUGAR depende dele — o
  //    `useState` de verdade está no bloco do quiosque, mais abaixo.
  const [quiosques, setQuiosques] = useState([]);
  // ⚠️ NASCE PREENCHIDO vindo do adesivo, pela mesma razão da loja: o seletor de
  //    equipamento é recortado pelo LUGAR desde 31/08/2026, então um POS de CK
  //    com o quiosque vazio ficaria fora da própria lista — e o `<select>`
  //    apareceria em branco. Vazio = o próprio prédio, que é o caso da maioria.
  const [quiosque, setQuiosque] = useState(etiqueta?.ativo?.quiosque || '');

  // ---------------------------------------------------------------------
  // OS TRÊS RECORTES DO SELETOR, EM CASCATA (o do LUGAR entrou em 31/08/2026)
  // ---------------------------------------------------------------------
  // ⚠️ NESTA ORDEM, e ela não é arbitrária:
  //   1) LUGAR    — o que a pessoa respondeu em "Para onde é o chamado". É o
  //                 recorte mais forte: define o universo do restante.
  //   2) FAMÍLIA  — o que a ficha do guia indicou. Ela não pediu, então é
  //                 anunciado e tem saída.
  //   3) BUSCA    — o que ela digitou agora.
  // Inverter 2 e 3 faria a busca rodar sobre a loja inteira e o recorte jogar
  // fora o que ela achou: digitaria "grill" e veria zero, com a impressora Grill
  // existindo.
  //
  // ⚠️⚠️ `ativosDoLocal` é o denominador de TODO texto da tela ("ver todos os N",
  //    "esse lugar não tem equipamento"). Usar `ativos.length` ali anunciaria os
  //    165 da loja para quem está olhando os 6 de um CK.
  const ativosDoLocal = filtrarPorQuiosque(ativos, quiosque);
  const ativosVisiveis = filtrarAtivos(
    filtrarPorFamilia(ativosDoLocal, ignorarFiltro ? null : familiaFiltro),
    buscaAtivo,
  );
  // ---------------------------------------------------------------------
  // O QUIOSQUE — "para onde o chamado é" (pedido da Julia, 2026-08-27).
  //
  // Oito das treze lojas têm Dessert Center (CK) além do prédio, e o técnico que
  // vai ao lugar errado perde a visita inteira.
  //
  // ⚠️ MEDIDO em 31/08/2026: **21 quiosques vigentes** em BVV(3), GTV(1), MAL(4),
  //    MOX(3), PCI(1), PCS(3), SVV(4) e VSH(2) — ANV, FER, RDP, SED e VEL não
  //    têm. Este comentário dizia "MAL, PCI e GTV", e estava errado: a lista sai
  //    da API a cada troca de loja, então a tela nunca errou — mas quem leu o
  //    comentário para decidir algo, sim.
  //
  // ⚠️ NASCE VAZIO = a própria loja, que é o caso da esmagadora maioria dos
  //    chamados (e o único possível nas outras cinco lojas).
  // ⚠️ A LISTA VEM DA API (`/ativos/quiosques`), nunca escrita aqui: ela sai da
  //    `dtbCadastroLojas`, onde o quiosque tem data de fechamento. Uma cópia no
  //    código ofereceria para sempre um CK que fechou — e, pior, ofereceria
  //    justamente o que o servidor recusa no submit.
  //
  // ⚠️⚠️ DESDE 31/08/2026 A LISTA É DE OBJETOS `{ sufixo, nome, apelido }`, e a
  //    distinção é o ponto: o seletor MOSTRA o `apelido` ('MAL - CK L3', da
  //    coluna `apelido_ck`) e MANDA o `sufixo` ('CK 3'). Mandar o apelido gravaria
  //    texto de negócio numa coluna que é chave de lugar — e o apelido nem é
  //    único (as duas pontas de uma sucessão dividem o mesmo).
  //
  // ⚠️⚠️ E ELE PASSOU A RECORTAR O SELETOR DE EQUIPAMENTO (ver `ativosDoLocal`
  //    lá em cima). Antes o quiosque só dizia para onde o técnico ia; agora ele
  //    também responde "quais peças existem nesse lugar".
  //
  // ⚠️ Os dois `useState` ficam LÁ EM CIMA, junto do recorte que depende deles.
  // ---------------------------------------------------------------------
  // ⚠️⚠️ TEMPORÁRIO ATÉ 18/09/2026 — a data de abertura digitada.
  //    'AAAA-MM-DD' ou vazio; vazio (o normal) = o chamado nasce agora.
  //    Ver `chamados-migracao-auvo.js` para o porquê e para como desligar.
  const [dataAbertura, setDataAbertura] = useState('');
  // ⚠️ Calculada UMA VEZ, na montagem: `janelaMigracaoAberta()` a cada render
  //    faria o campo sumir no meio do preenchimento de quem virasse a
  //    meia-noite do dia 18 com o formulário aberto. Quem decide de verdade é o
  //    servidor — se a aba ficou velha, o envio volta 400 mandando recarregar.
  const [migracaoAberta] = useState(() => janelaMigracaoAberta());
  // ⚠️⚠️ O CAMPO É DO `Gerente TI` E DO `Admin`, não da loja (Julia, 10/09/2026):
  //    quem transcreve o que estava no Auvo é o TI, e o gerente não deve ter na
  //    tela um campo capaz de mudar o prazo do próprio chamado dele.
  //    Esconder aqui é ATALHO — quem recusa de verdade é o `POST /`, com 403.
  const podeMigrar = podeInformarAbertura(
    perfilNoModulo(JSON.parse(localStorage.getItem('user') || '{}')?.perfil, 'chamados')
  );
  // O bloco âmbar só aparece para quem pode E enquanto a janela durar.
  const mostrarCampoAbertura = migracaoAberta && podeMigrar;
  // ⚠️ `dataLocalISO` e NUNCA `toISOString()`: este valor é o `max` do input, e
  //    o `toISOString` converte para UTC — na madrugada brasileira o teto
  //    voltaria como o dia ANTERIOR, e quem migrasse à noite não conseguiria
  //    escolher a data de hoje.
  const hojeISO = dataLocalISO(new Date());

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [fotos, setFotos] = useState([]);          // [{ url, nome }]
  const [subindo, setSubindo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  // O chamado recém-aberto. Enquanto houver um aqui, a tela mostra a CONFIRMAÇÃO
  // no lugar do formulário — ver o porquê no `enviar`.
  const [aberto, setAberto] = useState(null);   // { id, local }

  // O parque da loja. `?ativos=1` porque equipamento desativado não deve ser
  // escolhível — o backend já recorta pela loja de quem está logado.
  //
  // ⚠️⚠️ "NÃO CARREGOU" E "ESTÁ VAZIO" SÃO ESTADOS DIFERENTES, e misturá-los foi
  //    um erro meu na primeira versão: o `catch` zerava a lista em silêncio, então
  //    falha de rede, sessão expirada e loja sem equipamento cadastrado produziam
  //    a MESMA tela — um seletor vazio sem explicação. É o mesmo princípio do
  //    "Não enviado" × "Erro" da Pesquisa de Escala: o que se conserta em cada
  //    caso é diferente, então a tela precisa dizer qual é.
  const [erroAtivos, setErroAtivos] = useState('');
  const [carregandoAtivos, setCarregandoAtivos] = useState(true);

  const carregarAtivos = useCallback(async () => {
    setCarregandoAtivos(true); setErroAtivos('');
    try {
      // ⚠️⚠️ `daLoja` NÃO É REDUNDANTE com o recorte do backend (Julia, 27/08).
      //    O `recorteLoja` do servidor devolve `null` para Admin, Gerente TI, TI
      //    e para quem é do ESCRITÓRIO — eles veem o parque da rede inteira, o
      //    que está certo na tela de CADASTRO e errado aqui: o chamado nasce
      //    sempre para UMA loja, e oferecer o equipamento das outras é oferecer
      //    o que o `POST` depois recusa ("Esse equipamento é de outra loja").
      // ⚠️ É a MESMA loja do chamado que vai ser aberto — e desde 28/08 ela pode
      //    ser ESCOLHIDA (`lojaAlvo`), não é mais sempre a de quem abre. Trocar a
      //    loja recarrega esta lista; era o que o comentário antigo previa.
      // ⚠️⚠️ UM CATÁLOGO, DUAS FILAS (17/09/2026): o equipamento das duas vive na
      //    `dtbChamadoAtivo`, separado pela coluna `DEPARTAMENTO` — a rota é a
      //    mesma e o que muda é o filtro. (Houve, por algumas horas deste dia, um
      //    desenho com duas tabelas e uma rota `/manutencao` própria; ele caiu
      //    junto com a ideia de usar o cadastro da Certificação como catálogo.)
      // ⚠️ O departamento vai SEMPRE, inclusive em TI: sem ele a abertura
      //    ofereceria o parque das duas filas no mesmo seletor — equipamento
      //    real, fila errada, e o `POST` recusando depois de a pessoa escolher.
      const r = await fetch(
        `/api/chamados/ativos?ativos=1&departamento=${encodeURIComponent(equipe)}`
        + `&daLoja=${encodeURIComponent(lojaAlvo || '')}`,
        { headers: auth() }
      );
      if (r.status === 401) throw new Error('Sua sessão expirou. Saia e entre de novo.');
      if (r.status === 403) throw new Error('Seu perfil não tem acesso à lista de equipamentos.');
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.message || `A lista de equipamentos não carregou (HTTP ${r.status}).`);
      }
      setAtivos(await r.json());
    } catch (e) {
      // O equipamento é opcional, então o chamado ainda pode ser aberto — mas o
      // motivo aparece na tela em vez de virar um seletor misteriosamente vazio.
      console.error('[chamados] equipamentos:', e.message);
      setErroAtivos(e.message);
      setAtivos([]);
    } finally {
      setCarregandoAtivos(false);
    }
    // ⚠️ Depende de `lojaAlvo`, que AGORA MUDA (o seletor de loja): trocar a
    //    loja tem de trocar o parque oferecido. Era `user?.loja`, que nunca
    //    mudava durante a sessão.
    // ⚠️ E de `ehManutencao`, que decide QUAL catálogo é buscado: sem ele na
    //    lista, a tela que trocasse de fila sem remontar continuaria oferecendo o
    //    parque da fila anterior — equipamento certo, fila errada.
    // ⚠️ `equipe` e não `ehManutencao`: é ela que vai no `?departamento=`. Uma
    //    fila nova amanhã mudaria o parâmetro sem mudar o booleano, e a lista
    //    continuaria oferecendo o catálogo anterior.
  }, [lojaAlvo, equipe]);

  useEffect(() => { carregarAtivos(); }, [carregarAtivos]);

  // As lojas para as quais esta pessoa pode abrir chamado.
  // ⚠️ Falha silenciosa é aceitável AQUI, e só aqui: sem a lista o seletor não
  //    aparece e a tela volta ao comportamento antigo (a loja de quem abre), que
  //    é o certo para 99% de quem usa esta tela. Travar a abertura porque o
  //    seletor de loja não carregou seria trocar um incômodo por um bloqueio.
  useEffect(() => {
    fetch(`${API}/lojas`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((lista) => setLojas(Array.isArray(lista) ? lista : []))
      .catch(() => setLojas([]));
  }, []);

  // ⚠️⚠️ TROCAR DE LOJA LIMPA O EQUIPAMENTO E O QUIOSQUE. Sem isto, o `CODIGO`
  //    escolhido na loja anterior continuaria selecionado — e o `POST` o
  //    recusaria com "Esse equipamento é de outra loja", num formulário que
  //    parece preenchido corretamente. O mesmo vale para o quiosque, que é uma
  //    lista por loja.
  //    ⚠️ `useAoMudar` e não `useEffect`: na MONTAGEM ninguém trocou de loja, e
  //    limpar ali apagava o equipamento que o QR Code preencheu (ver o helper).
  useAoMudar(lojaAlvo, () => {
    setIdAtivo('');
    setQuiosque('');
    setBuscaAtivo('');
  });

  // ⚠️⚠️ TROCAR O LUGAR LIMPA O EQUIPAMENTO, pela MESMA razão de trocar a loja
  //    (31/08/2026): desde que o seletor passou a ser recortado pelo quiosque, o
  //    `CODIGO` escolhido antes pode não estar mais na lista — e um `<select>`
  //    com `value` fora das opções renderiza EM BRANCO, sem avisar. A pessoa
  //    veria um campo vazio que ela jurava ter preenchido.
  // ⚠️ A busca também: o termo que achava a peça do salão devolve zero no CK, e
  //    lista vazia logo depois de trocar de lugar parece "o CK não tem nada".
  //    ⚠️ `useAoMudar` pelo mesmo motivo do efeito da loja, acima.
  useAoMudar(quiosque, () => {
    setIdAtivo('');
    setBuscaAtivo('');
  });

  // Os quiosques da loja de quem está logado.
  //
  // ⚠️ FALHA EM SILÊNCIO DE PROPÓSITO, e é o oposto do que se faz com os tipos e
  //    os equipamentos logo abaixo. Lá o silêncio é bug: sem tipo não se abre
  //    chamado nenhum, e o seletor de equipamento vazio parece loja sem parque.
  //    Aqui, lista vazia é o ESTADO NORMAL de cinco das treze lojas — o seletor
  //    simplesmente não aparece, e a abertura segue exatamente como sempre foi.
  //    Um aviso de erro nesse caminho assustaria a rede inteira por um campo
  //    que quase ninguém usa.
  // ⚠️ O preço, dito às claras: se a chamada falhar numa loja QUE TEM quiosque,
  //    o gerente abre para a loja sem perceber a opção. É recuperável (o TI
  //    corrige o chamado), ao contrário de travar a abertura.
  useEffect(() => {
    if (!lojaAlvo) return;
    fetch(`${API}/ativos/quiosques?loja=${encodeURIComponent(lojaAlvo)}`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((lista) => setQuiosques(Array.isArray(lista) ? lista : []))
      .catch(() => setQuiosques([]));
    // ⚠️ Segue a loja ESCOLHIDA desde 28/08 — era `[]` (uma vez só, com a loja
    //    de quem abre). Sem isto, o TI abrindo para a MAL veria os quiosques do
    //    escritório, que não tem nenhum.
  }, [lojaAlvo]);

  // Os tipos vêm da API, sem cópia aqui. ⚠️ É o que permite trocá-los mexendo em
  // UM lugar (`chamados-tipos-solicitacao.js`) — duas listas viriam a divergir, e
  // a tela passaria a oferecer opção que o servidor recusa.
  // ⚠️ Aqui a falha silenciosa é PIOR que na lista de equipamentos: o tipo é
  //    OBRIGATÓRIO, então lista vazia = não dá para abrir chamado nenhum. Um
  //    `.catch(() => [])` mudo transformaria uma falha de rede em "o portal não
  //    deixa abrir chamado", que é o diagnóstico errado.
  const [erroTipos, setErroTipos] = useState('');
  useEffect(() => {
    fetch(`${API}/tipos-solicitacao`, { headers: auth() })
      .then(async (r) => {
        if (r.status === 401) throw new Error('Sua sessão expirou. Saia e entre de novo.');
        if (!r.ok) throw new Error(`A lista de tipos não carregou (HTTP ${r.status}).`);
        return r.json();
      })
      .then((lista) => { setTipos(lista); setErroTipos(''); })
      .catch((e) => { console.error('[chamados] tipos:', e.message); setErroTipos(e.message); setTipos([]); });
  }, []);

  // A TABELA DE PRAZOS DO SLA, para a loja saber o que esperar (Julia, 09/09).
  //
  // ⚠️⚠️ VEM DA API pelo mesmo motivo dos tipos: o `PRAZOS` do `chamados-sla.js` é
  //    a única fonte, e uma cópia aqui divergiria dele no primeiro ajuste. O
  //    sintoma seria o pior possível — a tela PROMETENDO à loja um prazo que o
  //    indicador não usa para cobrar.
  // ⚠️ FALHA SILENCIOSA de propósito, ao contrário dos tipos: sem os prazos o
  //    bloco simplesmente não aparece e o formulário funciona igual. É
  //    informação de apoio, não requisito para abrir chamado — travar a abertura
  //    por causa dela seria trocar um incômodo por um impedimento.
  const [prazosSla, setPrazosSla] = useState(null);
  useEffect(() => {
    fetch(`${API}/sla/prazos`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setPrazosSla(d && Array.isArray(d.faixas) && d.faixas.length ? d : null))
      .catch(() => setPrazosSla(null));
  }, []);

  /**
   * O nome do lugar COMO A OPERAÇÃO FALA: 'MAL - CK L3', ou só 'MAL' sem quiosque.
   *
   * ⚠️⚠️ MUDOU EM 31/08/2026 (Julia): mostrava a sigla do cadastro ('MAL CK 3') e
   *    passou a mostrar o `apelido_ck`. A numeração `CK 1..4` não significa nada
   *    fora do cadastro fiscal — ninguém na loja chama o ponto de "CK 3", chama
   *    de "o CK da L3" ou "o McCafé". Obrigar a decorar a numeração é o que fazia
   *    a pessoa escolher o quiosque errado, e o técnico ir ao lugar errado.
   *
   * ⚠️ FALLBACK EM CASCATA, e ele importa: apelido → sigla montada. Quiosque
   *    novo pode nascer sem `apelido_ck` (é preenchido à mão), e uma opção em
   *    branco no seletor é pior que uma sigla feia.
   *
   * ⚠️⚠️ ISTO É SÓ ESTA TELA. O que se GRAVA continua sendo o sufixo, e a fila, o
   *    PDF e o e-mail seguem dizendo 'MAL CK 3' (o `nomeCompletoLoja` do
   *    servidor, intocado). É deliberado: o pedido foi sobre a hora de ESCOLHER,
   *    que é quando a numeração atrapalha. Se um dia o apelido tiver de valer no
   *    módulo inteiro, o lugar de mudar é o `chamados-quiosques.js` — e aí muda
   *    para todos de uma vez.
   */
  const nomeDoLocal = (q) => {
    if (!q) return lojaAlvo || '—';
    const achado = quiosques.find((x) => x.sufixo === q);
    return achado?.apelido || `${lojaAlvo || ''} ${q}`.trim();
  };

  const subirFoto = async (arquivo) => {
    if (!arquivo) return;
    if (fotos.length >= MAX_FOTOS) { setErro(`Máximo de ${MAX_FOTOS} fotos.`); return; }
    setSubindo(true); setErro('');
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      fd.append('momento', 'Abertura');
      fd.append('loja', lojaAlvo || '');
      const r = await fetch(`${API}/upload-foto`, { method: 'POST', headers: auth(), body: fd });
      const dados = await r.json();
      if (!r.ok) throw new Error(dados.message || 'Falha ao enviar a imagem.');
      setFotos((f) => [...f, { url: dados.url, nome: arquivo.name }]);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSubindo(false);
    }
  };

  // Vários de uma vez, do gatilho de galeria. ⚠️ O teto é fatiado AQUI, uma só
  //    vez: o cap interno do `subirFoto` lê `fotos.length` do closure, que NÃO
  //    atualiza no meio do laço (o setState é assíncrono). Fatiar antes é o que
  //    garante o teto e permite avisar o que sobrou.
  const anexarFotos = async (arquivos) => {
    const restante = MAX_FOTOS - fotos.length;
    if (restante <= 0) { setErro(`Máximo de ${MAX_FOTOS} fotos.`); return; }
    if (arquivos.length > restante) {
      setErro(`Você pode anexar mais ${restante} — as demais foram ignoradas.`);
    }
    // Em série: o upload do backend é single (`upload.single`), e sequencial
    // mantém a ordem das fotos e não dispara N requisições ao SharePoint de uma vez.
    for (const a of arquivos.slice(0, restante)) {
      await subirFoto(a); // eslint-disable-line no-await-in-loop -- ver comentário acima
    }
  };

  /**
   * Devolve o formulário ao estado inicial.
   *
   * ⚠️ O QUIOSQUE VOLTA PARA A LOJA junto com o resto. Deixá-lo escolhido faria
   *    o próximo chamado nascer apontado para o CK sem ninguém ter pedido — e o
   *    campo estaria fora da vista, no meio do formulário.
   */
  // =====================================================================
  // O RASCUNHO DA ABERTURA (Julia, 2026-09-01) — no padrão da Certificação.
  //
  // ⚠️⚠️ O QUE ELE CONSERTA: o formulário pede tipo, equipamento, assunto,
  //    descrição e FOTO. Quem abre está de pé na loja, no celular, com o
  //    equipamento quebrado na frente — sai da tela para tirar a foto, atende
  //    alguém, a sessão cai. Até aqui, voltar significava recomeçar do zero, e
  //    recomeçar do zero é o caminho mais curto de volta para o WhatsApp.
  //
  // ⚠️⚠️ O RASCUNHO NÃO É UM CHAMADO, e essa é a decisão que sustenta o resto:
  //    ele mora em tabela própria (`dtbChamadoRascunho`), não como um status da
  //    `dtbChamado`. Um chamado "em rascunho" apareceria na fila, contaria como
  //    *Aberto* e poderia ser triado — e *Aberto* = "aguardando triagem" é
  //    justamente o número que o módulo existe para manter visível.
  //
  // ⚠️ A FOTO já está no SharePoint quando entra aqui (ela sobe antes de o
  //    chamado existir, e é por isso que o módulo está em `ALVOS` do
  //    `limpar-fotos-orfas.js`). O rascunho guarda só a URL — abandonar o
  //    rascunho deixa arquivo órfão, que a limpeza recolhe. Mecanismo igual,
  //    janela maior.
  // =====================================================================
  const [rascunhoLido, setRascunhoLido] = useState(false);
  const [rascunhoDe, setRascunhoDe] = useState(null);   // quando foi salvo, p/ o aviso

  // ⚠️ O snapshot é o que decide SE grava. Ele leva os campos do formulário e
  //    mais nada — listas carregadas da API (ativos, tipos, lojas) mudariam a
  //    string sem o usuário ter digitado, e o autosave dispararia sozinho.
  const snapshotRascunho = JSON.stringify({
    tipoSolicitacao, idAtivo, titulo, descricao, lojaAlvo, quiosque,
    fotos: fotos.map((f) => f.url),
    // O pedido de imagem também é rascunho: ele é o formulário mais longo de
    // preencher (período + motivo), e é justo o que mais dói perder.
    canais, periodoInicio, periodoFim, destinoImagem, motivoImagem,
    // O pedido de chip também é rascunho (DEM-00027).
    colaboradorChip, motivoChip, linhaChip, obsChip,
  });

  // Lê uma vez, ao montar. ⚠️ `finally` sempre marca como lido: se a leitura
  // falhar, o formulário abre em branco e funciona — o que não pode é ficar
  // travado esperando um rascunho que nunca vem.
  useEffect(() => {
    let vivo = true;
    fetch(`${API}/rascunho?contexto=abertura`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : { rascunho: null }))
      .then((d) => {
        if (!vivo || !d.rascunho) return;
        const g = d.rascunho;
        // ⚠️ Cada campo com o seu fallback: rascunho de uma versão anterior da
        //    tela não pode gravar `undefined` num input controlado (o React
        //    troca para não-controlado e reclama no console).
        setTipoSolicitacao(g.tipoSolicitacao || '');
        // ⚠️ Cada um com o seu fallback, como o resto: rascunho gravado ANTES
        //    deste tipo existir não tem estes campos, e `undefined` num input
        //    controlado faz o React trocá-lo para não-controlado.
        setCanais(Array.isArray(g.canais) ? g.canais : []);
        setPeriodoInicio(g.periodoInicio || '');
        setPeriodoFim(g.periodoFim || '');
        setDestinoImagem(g.destinoImagem || '');
        setMotivoImagem(g.motivoImagem || '');
        // Os campos do chip, cada um com o seu fallback (DEM-00027).
        setColaboradorChip(g.colaboradorChip || '');
        setMotivoChip(g.motivoChip || '');
        setLinhaChip(g.linhaChip || '');
        setObsChip(g.obsChip || '');
        // ⚠️⚠️ O ADESIVO GANHA DO RASCUNHO, e esquecer isto é o bug mais provável
        //    de todo o mecanismo: o rascunho carrega ASSÍNCRONO, então ele chega
        //    DEPOIS do estado inicial e sobrescreveria o equipamento que a pessoa
        //    acabou de escanear — com o aparelho de um chamado que ela abandonou
        //    dias atrás, e sem nada na tela dizendo o que aconteceu.
        if (!etiqueta) setIdAtivo(g.idAtivo || '');
        setTitulo(g.titulo || '');
        setDescricao(g.descricao || '');
        // ⚠️⚠️ O ADESIVO GANHA DO RASCUNHO AQUI TAMBÉM, e este era o segundo
        //    caminho do bug de 11/09: o rascunho chega assíncrono e trocar o
        //    LUGAR limpa o equipamento (ver `useAoMudar` do quiosque) — então o
        //    rascunho, mesmo sem tocar no equipamento, apagava o que o QR Code
        //    havia preenchido, de lambuja.
        if (!etiqueta) setQuiosque(g.quiosque || '');
        if (g.lojaAlvo && !etiqueta) setLojaAlvo(g.lojaAlvo);
        // ⚠️ O nome do arquivo não volta (só a URL foi guardada) — a tela mostra
        //    "Foto anexada", que é o que importa: a evidência está lá.
        setFotos(Array.isArray(g.fotos) ? g.fotos.map((url) => ({ url, nome: 'Foto anexada' })) : []);
        setRascunhoDe(d.atualizadoEm || null);
      })
      .catch(() => {})
      .finally(() => { if (vivo) setRascunhoLido(true); });
    return () => { vivo = false; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps -- só na montagem, de propósito

  // =====================================================================
  // O RASCUNHO NÃO PODE RESSUSCITAR UMA LOJA QUE A PESSOA NÃO USA MAIS
  // =====================================================================
  // ⚠️⚠️ Bug medido em 14/09/2026: um usuário teve a loja trocada de VEL para
  //    MOX no cadastro e TODAS as telas do portal passaram a mostrar MOX —
  //    menos esta, que insistia em VEL. Não era autenticação: o `lojaAlvo`
  //    nasce certo (`user?.loja`, lá em cima) e o rascunho, que chega
  //    ASSÍNCRONO e é mais antigo que a troca, sobrescrevia com a loja velha.
  //    O rascunho não expira, então ele guarda para sempre o que era verdade
  //    no dia em que foi salvo.
  //
  // ⚠️ O estrago não para na loja errada: um `<select>` com `value` fora das
  //    opções renderiza EM BRANCO (a mesma armadilha já documentada no efeito
  //    do quiosque), e o `POST` é recusado pelo servidor — que recorta por
  //    loja — sem nada na tela explicando o porquê. Quem vive isso é o
  //    gerente, de pé na loja, no celular.
  //
  // ⚠️ Espera as DUAS chamadas: validar dentro do efeito do rascunho não
  //    funciona, porque `/rascunho` e `/lojas` são independentes e qualquer uma
  //    pode chegar primeiro. `rascunhoLido` marca uma; `lojas.length` marca a
  //    outra.
  //
  // ⚠️ `lojas` VAZIA não corrige nada, e isso é de propósito: a lista falha em
  //    silêncio (ver o efeito que a carrega), e vazio ali significa "não sei
  //    quais são", não "nenhuma vale". Corrigir nesse estado trocaria uma loja
  //    boa pela do cadastro por causa de uma falha de rede.
  //
  // ⚠️ O ADESIVO NÃO ENTRA AQUI, pela mesma regra dos outros dois pontos que o
  //    tratam: quem acabou de escanear uma etiqueta fez um ato do presente, e
  //    trocar a loja dele por baixo dos panos é o bug que este arquivo já
  //    documenta. Etiqueta de uma loja que a pessoa não alcança segue sendo
  //    recusada pelo servidor, que é onde a regra mora.
  //
  // Trocar o `lojaAlvo` aciona o `useAoMudar` da loja, que limpa equipamento,
  // quiosque e busca — e é exatamente o que se quer: eles eram da loja velha.
  useEffect(() => {
    if (etiqueta || !rascunhoLido || !lojas.length) return;
    if (!lojaAlvo || lojas.includes(lojaAlvo)) return;
    // A loja do cadastro é o destino natural; `lojas[0]` cobre o caso de ela
    // também não estar na lista (recorte mudou, cadastro no ESCRITÓRIO...) —
    // sem isso a tela ficaria com o seletor em branco, que é o sintoma original.
    setLojaAlvo(lojas.includes(user?.loja) ? user.loja : lojas[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `user` sai do localStorage a cada render; depender dele re-rodaria à toa
  }, [rascunhoLido, lojas, lojaAlvo, etiqueta]);

  const salvarRascunho = useCallback(async () => {
    const r = await fetch(`${API}/rascunho`, {
      method: 'PUT',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ contexto: 'abertura', conteudo: JSON.parse(snapshotRascunho) }),
    });
    if (!r.ok) throw new Error('Não foi possível salvar o rascunho.');
  }, [snapshotRascunho]);

  /**
   * Apaga o rascunho no servidor.
   *
   * ⚠️⚠️ CHAMADO DEPOIS DE ENVIAR, e esquecer isso é o bug mais provável do
   *    mecanismo: o formulário voltaria preenchido na visita seguinte e a pessoa
   *    abriria O MESMO CHAMADO DUAS VEZES. O backend também apaga no `POST` —
   *    cinto e suspensório, porque aqui o `catch` é mudo.
   */
  const descartarRascunho = useCallback(async () => {
    setRascunhoDe(null);
    await fetch(`${API}/rascunho?contexto=abertura`, { method: 'DELETE', headers: auth() })
      .catch(() => {});
  }, []);

  // ⚠️⚠️ `ativo` só depois de LER: sem esta trava o autosave dispararia com o
  //    formulário ainda vazio e gravaria por cima do rascunho que estava sendo
  //    carregado — apagando exatamente o que a pessoa voltou para buscar.
  // ⚠️ E não salva na tela de confirmação (`aberto`), onde o formulário já foi
  //    limpo: seria gravar um rascunho em branco por cima de nada.
  const { estado: autosaveEstado } = useAutosaveRascunho({
    ativo: rascunhoLido && !aberto && !enviando,
    snapshot: snapshotRascunho,
    salvarFn: salvarRascunho,
  });

  const limparFormulario = () => {
    setTitulo(''); setDescricao(''); setIdAtivo(''); setFotos([]);
    setTipoSolicitacao(''); setQuiosque('');
    setCanais([]); setPeriodoInicio(''); setPeriodoFim('');
    setDestinoImagem(''); setMotivoImagem('');
    setColaboradorChip(''); setMotivoChip(''); setLinhaChip(''); setObsChip('');
    // ⚠️ A data de migração também zera: quem transcreve vários chamados do
    //    Auvo em sequência precisa RE-INFORMAR a data a cada um. Mantê-la seria
    //    pior — o segundo chamado herdaria calado a data do primeiro.
    setDataAbertura('');
  };

  const enviar = async () => {
    setErro('');
    if (!tipoSolicitacao) { setErro('Escolha o tipo de solicitação.'); return; }
    // ⚠️ Obrigatório desde 28/08, quando a opção "Não sei / não se aplica" saiu
    //    do seletor (Julia). ⚠️ A trava é SÓ DE TELA — o backend segue aceitando
    //    chamado sem equipamento; ver o comentário no `<option>` do seletor.
    // ⚠️ A Solicitação de Imagem não exige equipamento: o que ela pede é a
    //    gravação do conjunto de câmeras, e o parque registra o DVR como UM
    //    ativo que nem toda loja tem cadastrado. Exigi-lo aqui travaria o pedido
    //    por um cadastro que não é do pedido. O backend já o trata como opcional.
    if (!idAtivo && !ehImagem && !ehChip) { setErro('Escolha o equipamento com problema.'); return; }
    // ⚠️ Não valem na Solicitação de Imagem nem no Chip: os campos nem existem na
    //    tela, e quem os escreve é o servidor a partir do pedido estruturado.
    if (!ehImagem && !ehChip && !titulo.trim()) { setErro('Diga em poucas palavras qual é o problema.'); return; }
    if (!ehImagem && !ehChip && !descricao.trim()) { setErro('Descreva o que está acontecendo.'); return; }
    // ⚠️⚠️ A foto é dispensada no Imagem e no Chip — ver o comentário do estado.
    if (!ehImagem && !ehChip && fotos.length < MIN_FOTOS) {
      setErro('Anexe ao menos uma foto do problema — é o que poupa o técnico de descobrir na loja.');
      return;
    }
    if (ehImagem) {
      // ⚠️ Atalho de tela: poupa o ida-e-volta. A REGRA (teto por quiosque,
      //    janela, formato) é do servidor, em `chamados-imagem.js`.
      if (!canais.length) { setErro('Marque ao menos uma câmera.'); return; }
      if (!periodoInicio || !periodoFim) { setErro('Informe o período da imagem (início e fim).'); return; }
      if (periodoInicio >= periodoFim) { setErro('O início do período tem de ser anterior ao fim.'); return; }
      if (!destinoImagem) { setErro('Escolha o destino da imagem.'); return; }
      if (!motivoImagem.trim()) { setErro('Diga por que a imagem está sendo pedida.'); return; }
    }
    if (ehChip) {
      // ⚠️ Atalho de tela: a REGRA (obrigatoriedade condicional da linha) é do
      //    servidor, em `chamados-chip.js`. Aqui só se poupa o ida-e-volta.
      if (!colaboradorChip.trim()) { setErro('Diga para qual colaborador é o chip.'); return; }
      if (!motivoChip) { setErro('Escolha o motivo do pedido.'); return; }
      if (motivoExigeLinha(motivoChip) && !linhaChip.trim()) {
        setErro('Informe a linha/número atual — é obrigatória para troca, 2ª via e cancelamento.');
        return;
      }
    }
    // ⚠️ TEMPORÁRIO ATÉ 18/09: a MESMA função que o servidor usa (é o espelho).
    //    Aqui ela é atalho — poupa o ida-e-volta —, lá é a regra. Se um dia as
    //    duas discordarem, quem vale é o servidor, e o teste de espelho reprova.
    if (dataAbertura) {
      try { resolverAberturaInformada(dataAbertura, new Date()); }
      catch (e) { setErro(e.message); return; }
    }

    setEnviando(true);
    try {
      const r = await fetch(API, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // ⚠️ A FILA do chamado. Ausente, o servidor assume 'TI' — é o que
          //    mantém a tela antiga funcionando na janela de deploy.
          equipe,
          tipo_solicitacao: tipoSolicitacao,
          // ⚠️ Vazio na Solicitação de Imagem — o servidor os GERA e descarta o
          //    que vier aqui. Mandar resto de um rascunho anterior seria mandar
          //    um texto que ninguém escreveu para este chamado.
          titulo: (ehImagem || ehChip) ? '' : titulo.trim(),
          descricao: (ehImagem || ehChip) ? '' : descricao.trim(),
          loja: lojaAlvo || '',
          // ⚠️ Vazio = a própria loja. O servidor entende assim e grava NULL —
          //    não é campo faltando, é a maioria dos chamados.
          quiosque: quiosque || null,
          // ⚠️ O chip não leva equipamento (o servidor recusa se vier).
          id_ativo: ehChip ? null : (idAtivo || null),
          // ⚠️ TEMPORÁRIO ATÉ 18/09. Vazio = null = o chamado nasce agora, que é
          //    o caso normal. Depois da janela o campo nem é renderizado.
          aberto_em: dataAbertura || null,
          // ⚠️ O chip dispensa foto (pedido administrativo).
          fotos: ehChip ? [] : fotos.map((f) => f.url),
          // ⚠️ Os campos do pedido de imagem só VÃO neste tipo — o servidor
          //    RECUSA (400) o bloco quando o tipo é outro, em vez de ignorá-lo.
          ...(ehImagem ? {
            canais,
            periodo_inicio: periodoInicio,
            periodo_fim: periodoFim,
            destino: destinoImagem,
            motivo: motivoImagem.trim(),
          } : {}),
          // ⚠️ Os campos do pedido de chip só VÃO neste tipo (DEM-00027).
          ...(ehChip ? {
            colaborador: colaboradorChip.trim(),
            motivo: motivoChip,
            linha_atual: linhaChip.trim(),
            observacao: obsChip.trim(),
          } : {}),
        }),
      });
      const dados = await r.json();
      if (!r.ok) throw new Error(dados.message || 'Falha ao abrir o chamado.');
      // ⚠️⚠️ O FORMULÁRIO DÁ LUGAR A UMA TELA DE CONFIRMAÇÃO (Julia, 27/08).
      //    Antes daqui saía um aviso verde e, 1,6s depois, um salto automático
      //    para a fila. Dois problemas: o salto levava para uma aba que a loja
      //    NÃO enxerga mais (a fila está escondida dela), e quem ficava não tinha
      //    o que fazer em seguida além de rolar de volta e recomeçar.
      //    Agora o número do chamado fica em tela, parado, com os caminhos
      //    possíveis à vista — abrir outro, ou ir acompanhar.
      // ⚠️ O quiosque entra no texto: é a última chance de notar que o chamado
      //    foi para o lugar errado, e agora ele não some sozinho da tela.
      // ⚠️ `dataAuvo` viaja junto para a confirmação poder DIZER que a data
      //    foi retroagida — é a última chance de a loja notar que digitou
      //    a data errada, e depois daqui o formulário é limpo.
      setAberto({ id: dados.id, local: nomeDoLocal(quiosque), dataAuvo: dataAbertura || null });
      // ⚠️⚠️ O RASCUNHO MORRE AQUI. Sem isso o formulário voltaria preenchido na
      //    próxima visita e a pessoa abriria o mesmo chamado duas vezes, achando
      //    que o primeiro não tinha ido. O backend também apaga no `POST`.
      await descartarRascunho();
      limparFormulario();
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  // =====================================================================
  // A TELA DE CONFIRMAÇÃO (Julia, 2026-08-27).
  //
  // Substitui o formulário inteiro depois que o chamado é aberto — não é um
  // aviso no topo dele.
  //
  // ⚠️⚠️ O QUE ELA CONSERTA: antes daqui saía um aviso verde e, 1,6s depois, um
  //    salto automático para a fila. O salto levava a uma aba que a LOJA não
  //    enxerga mais (a fila está escondida dela), e quem não saltava ficava
  //    diante de um formulário vazio, sem saber se o chamado tinha ido ou se
  //    aquilo era um formulário novo. Duas dúvidas onde deveria haver um número.
  //
  // ⚠️ O NÚMERO DO CHAMADO É O PROTAGONISTA: é ele que a loja repete ao telefone
  //    e cola no WhatsApp. Fica grande, sozinho, e não some sozinho da tela.
  //
  // ⚠️ Substituir o formulário (em vez de mostrar o aviso acima dele) é o que
  //    impede o duplo envio: sem campos à vista, não há o que reenviar.
  // =====================================================================
  if (aberto) {
    return (
      <div style={{ maxWidth: 780 }}>
        <div style={{
          backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12,
          padding: ehMobile ? 20 : 32, textAlign: 'center',
        }}>
          <CheckCircle2 size={44} color="#16a34a" style={{ marginBottom: 10 }} />
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#166534', margin: '0 0 6px 0' }}>
            Chamado aberto
          </h2>
          <p style={{
            fontSize: 34, fontWeight: 800, color: '#166534', margin: '0 0 6px 0',
            fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em',
          }}>
            #{aberto.id}
          </p>
          <p style={{ fontSize: 14, color: '#15803d', margin: '0 0 4px 0' }}>
            {aberto.local} · o TI foi avisado e vai definir a prioridade e o técnico.
          </p>
          {/* ⚠️ TEMPORÁRIO ATÉ 18/09 — só aparece em chamado migrado do Auvo.
              Repetir a data aqui é o que permite pegar o dedo errado ANTES de a
              pessoa sair da tela: corrigir depois exige o TI. */}
          {aberto.dataAuvo && (
            <p style={{
              fontSize: 13, color: '#92400e', margin: '0 0 4px 0', fontWeight: 700,
            }}>
              Migrado do Auvo, registrado como aberto em {formatarBR(aberto.dataAuvo)}.
            </p>
          )}
          {/* ⚠️ Diz o que acontece A SEGUIR. Sem isso, "aberto" é um beco: a loja
              não sabe se precisa fazer mais alguma coisa, e liga para o TI para
              perguntar — que é a ligação que o módulo existe para evitar. */}
          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 22px 0', lineHeight: 1.5 }}>
            Anote o número: é por ele que o chamado é encontrado. Você não precisa
            fazer mais nada — o técnico procura a loja.
          </p>

          <div style={{
            display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap',
          }}>
            {/* ⚠️ "Abrir outro" vem PRIMEIRO e é o botão cheio: quem acabou de
                abrir um chamado tem mais chance de abrir outro (a mesma pane
                costuma derrubar dois equipamentos) do que de ir consultar. */}
            <button
              type="button"
              onClick={() => { setAberto(null); setErro(''); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 20px', minHeight: 46, borderRadius: 8, border: 'none',
                backgroundColor: COR, color: '#fff', fontSize: 14, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <Send size={16} /> Abrir outro chamado
            </button>

            {/* ⚠️⚠️ SÓ APARECE PARA QUEM ENXERGA A FILA. Hoje a Consulta está
                escondida de `Gerente` e `Gerente Plantao` (o módulo ainda está em
                desenvolvimento para a loja), e o hub não passa esta prop a eles.
                Oferecer o botão assim mesmo levaria a uma aba inexistente — o
                `useEffect` do hub devolveria a pessoa à Visão Geral, engolindo
                esta confirmação no caminho.
                ⚠️ Quando a cortina cair, o botão aparece sozinho: nada aqui
                precisa mudar. */}
            {onIrParaConsulta && (
              <button
                type="button"
                onClick={onIrParaConsulta}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 20px', minHeight: 46, borderRadius: 8,
                  border: `1px solid ${COR}`, backgroundColor: '#fff', color: COR,
                  fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <ListChecks size={16} /> Ir para o controle de chamados
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    // `maxWidth` só limita no computador — no celular a tela já é mais estreita
    // que isso, e o formulário ocupa a largura disponível.
    <div style={{ maxWidth: 780 }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>Abrir chamado</h2>
      {/* ⚠️ O subtítulo acompanha o QUIOSQUE escolhido: escolher "MAL CK 3" lá
          embaixo e continuar lendo "Loja MAL" aqui em cima faria duvidar de qual
          das duas valeu. */}
      <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 16px 0' }}>
        Loja <strong style={{ color: COR }}>{nomeDoLocal(quiosque)}</strong> · aberto por {user?.nome || '—'}
      </p>

      {/* ---------------------------------------------------------------
          O RASCUNHO RETOMADO.
          ⚠️⚠️ ELE PRECISA SER ANUNCIADO. Um formulário que abre preenchido sem
             explicação é assustador — a pessoa não sabe se aquilo é um chamado
             que ela já mandou, se é de outra pessoa, ou se o sistema inventou.
             O aviso responde as três coisas: é seu, é rascunho, e ainda não foi
             enviado.
          ⚠️ Some assim que ela edita qualquer coisa (o `autosaveEstado` sai de
             'ocioso'): a partir daí o que está na tela é o de agora, não o que
             foi retomado, e o aviso viraria mentira.
          --------------------------------------------------------------- */}
      {rascunhoDe && autosaveEstado === 'ocioso' && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
          backgroundColor: COR_CLARA, border: `1px solid ${COR_A33}`, borderRadius: 8,
          padding: '10px 12px', marginBottom: 16,
        }}>
          <FileClock size={16} color={COR} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: '#3b0764', flex: 1, minWidth: 200 }}>
            Retomamos o rascunho que você deixou em <strong>{fmtQuando(rascunhoDe)}</strong>.
            Ele <strong>ainda não foi enviado</strong>.
          </span>
          <button
            type="button"
            onClick={() => { descartarRascunho(); limparFormulario(); }}
            style={{
              background: 'none', border: `1px solid ${COR_A55}`, color: COR, cursor: 'pointer',
              borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600,
            }}
          >
            Descartar e começar de novo
          </button>
        </div>
      )}

      {/* ⚠️ O aviso de gravação é discreto de propósito: ele tranquiliza quem
          olha, sem competir com o formulário. "Erro" é o único que precisa
          saltar — é ele que avisa que sair da tela agora custa o preenchimento. */}
      {autosaveEstado !== 'ocioso' && (
        <p style={{
          fontSize: 12, margin: '0 0 16px 0',
          color: autosaveEstado === 'erro' ? '#b91c1c' : '#64748b',
          fontWeight: autosaveEstado === 'erro' ? 600 : 400,
        }}>
          {autosaveEstado === 'salvando' && 'Salvando rascunho…'}
          {autosaveEstado === 'salvo' && 'Rascunho salvo — dá para sair e voltar depois.'}
          {autosaveEstado === 'erro' && 'Não conseguimos salvar o rascunho. Se sair agora, perde o preenchimento.'}
        </p>
      )}

      {/* ⚠️⚠️ O PASSO 1 É SÓ DA FILA DE T.I (Julia, 17/09/2026). O GPS-TI é o guia
          de suporte da equipe de TI — contato, escala e os procedimentos de POS,
          KVS, rede e pinpad. Mostrá-lo a quem abre chamado de Manutenção põe, no
          topo da tela, um "consulte antes" que aponta para um documento que não
          fala do problema dele: no melhor caso é ruído, no pior manda a pessoa
          ler a coisa errada antes do ato que ela veio fazer.
          ⚠️ Quando a Manutenção tiver um guia próprio, o lugar dele é ESTE — o
          bloco já nasceu como espaço reservado na posição de "passo 1". */}
      {!ehManutencao && (
      <>
      {/* ---------------------------------------------------------------
          PASSO 1 — O GPS-TI, o guia de pesquisa de suporte do Sebastião.
          Entregue em 2026-08-27.

          ⚠️ Este bloco esperava por ele desde 21/08: nasceu como espaço
             reservado, na posição de "passo 1", justamente para que a chegada
             fosse só CONTEÚDO e não uma reforma da tela. Foi o que aconteceu.

          ⚠️⚠️ O DESTINO MUDOU NO MESMO DIA. O guia nasceu numa cópia PÚBLICA
             (`/gps-ti/index.html`, sem login, aberta em outra aba do navegador)
             e essa rota foi desligada horas depois, a pedido da Julia. Agora o
             botão leva à aba *Manual do TI* deste módulo.

          ⚠️ O que se perdeu com isso, e vale saber antes de "melhorar" esta
             tela: (a) o formulário não sobrevive mais à leitura, porque trocar
             de aba desmonta a abertura; (b) o guia não é mais instalável no
             tablet nem funciona sem rede — dentro do `<iframe sandbox>` do
             manual, o service worker se auto-desliga.

          ⚠️ E NÃO é porta: dá para rolar e abrir o chamado sem ler (decisão da
             Julia — "a etapa não será obrigatória, porém quero como inicial").
             Travar a leitura só ensinaria a clicar em "já li".
          --------------------------------------------------------------- */}
      <div style={{
        backgroundColor: '#fff', border: `1px solid ${COR_CLARA}`,
        borderLeft: `4px solid ${COR}`, borderRadius: 10, padding: 20, marginBottom: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
          <BookOpen size={20} color={COR} />
          <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
            Passo 1 — consulte o GPS-TI antes de abrir
          </p>
        </div>
        <p style={{ margin: '0 0 14px 0', fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
          O <strong>GPS-TI</strong> é o guia de pesquisa de suporte do TI: fichas com
          foto, passo a passo por equipamento e sintoma. Boa parte dos casos se resolve
          por ele, sem chamado. Se você seguir os passos e não resolver, abra o chamado
          abaixo — e conte na descrição <strong>o que já tentou</strong>.
        </p>
        {/* ⚠️⚠️ ERA UM LINK PARA `/gps-ti/index.html`, A CÓPIA PÚBLICA — que foi
            DESLIGADA em 27/08 (Julia: "tire essa rota pública, deixe só dentro
            do site"). O guia agora vive na aba *Manual do TI*, atrás do login.

            ⚠️ Virou BOTÃO e não link porque o destino deixou de ser um endereço:
            é uma aba deste mesmo módulo. `<a href>` para rota interna do React
            recarregaria a aplicação inteira e levaria junto o formulário
            preenchido — exatamente o que o "abre em outra aba" existia para
            evitar.

            ⚠️⚠️ E É AQUI QUE ESTÁ O CUSTO DA MUDANÇA: trocar de aba PERDE o que já
            foi digitado, porque a abertura desmonta. Antes, a leitura era em
            outra aba do navegador e o formulário ficava intacto. Por isso o
            botão fica no PASSO 1, antes de tudo, e o aviso abaixo é explícito —
            o pior desenho seria manter a promessa antiga ("não se perde") depois
            que ela deixou de valer. */}
        {onIrParaManual && (
          <button
            type="button"
            onClick={onIrParaManual}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '11px 18px', minHeight: 44, borderRadius: 8, border: 'none',
              backgroundColor: COR, color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 4px 6px var(--ch-sombra-leve)',
            }}
          >
            <BookOpen size={16} /> Abrir o GPS-TI
          </button>
        )}
        {/* ⚠️ Dito na tela porque muda o que a pessoa faz: se ela já começou a
            preencher, tem de terminar antes de ir ler. Omitir isso faria o
            gerente perder a descrição e as fotos no meio do chamado. */}
        <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
          O guia abre na aba <strong>Manual do TI</strong>, aqui no portal.
          Consulte-o <strong>antes</strong> de preencher: ao trocar de aba, o que já
          tiver sido digitado neste formulário se perde.
        </p>
      </div>
      </>
      )}

      <p style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 18px 0' }}>
        Passo 2 — abrir o chamado
      </p>

      {erro && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ margin: 0, fontSize: 13, color: '#991b1b' }}>{erro}</p>
        </div>
      )}

      {/* ================================================================
          A LOJA DO CHAMADO (Julia, 2026-08-28).

          ⚠️⚠️ SÓ APARECE PARA QUEM TEM MAIS DE UMA — na prática, o `Gerente TI` e
             o `TI`, que atendem a rede e recebem o chamado por telefone. Para o
             gerente de loja a lista vem com uma opção só e o seletor SOME: um
             campo com uma escolha possível é pergunta cuja resposta já se sabe.

          ⚠️ QUEM RECORTA É O BACKEND (`GET /lojas`), não esta condição. Aqui só
             se decide MOSTRAR ou não; se a tela oferecesse uma loja que o
             servidor recusa, o 403 apareceria no envio, com o formulário todo
             preenchido.

          ⚠️⚠️ TROCAR A LOJA LIMPA equipamento, quiosque e busca (ver o `useEffect`
             lá em cima). Sem isso, o equipamento da loja anterior seguiria
             escolhido e o `POST` responderia "esse equipamento é de outra loja"
             num formulário que parece certo.
          ================================================================ */}
      {podeEscolherLoja && (
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle} htmlFor="ch-loja">Loja do chamado</label>
          <select
            id="ch-loja"
            style={inputStyle}
            value={lojaAlvo}
            onChange={(e) => setLojaAlvo(e.target.value)}
          >
            {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0 0' }}>
            O chamado nasce para esta loja — é ela que aparece na fila e para onde
            o técnico vai. Trocar aqui recarrega os equipamentos.
          </p>
        </div>
      )}

      {/* ================================================================
          PARA ONDE É O CHAMADO — loja ou quiosque (Julia, 2026-08-27).

          ⚠️⚠️ SÓ APARECE NAS LOJAS QUE TÊM QUIOSQUE (oito das treze, medido em
             31/08/2026). Nas outras cinco, um seletor com uma opção só é ruído:
             pergunta cuja resposta é sempre a mesma, ocupando o primeiro lugar do
             formulário. É o mesmo desenho já usado no cadastro de equipamento.

          ⚠️ VEM PRIMEIRO, antes até do tipo — é a pergunta que muda para onde o
             técnico se desloca. Enterrada no meio do formulário, seria pulada
             justamente por quem já preencheu o resto no piloto automático.

          ⚠️⚠️ AS OPÇÕES MOSTRAM O APELIDO ('MAL - CK L3'), que é como a operação
             fala — mas o que viaja é só o SUFIXO ('CK 3'). Mostravam a sigla do
             cadastro ('MAL CK 3') até 31/08/2026; a numeração não significa nada
             fora do cadastro fiscal, e decorá-la é o que fazia escolher o CK
             errado. ⚠️ Guardar o apelido seria erro duplo: ele não é único e
             muda com o nome do shopping — e guardar 'MAL CK 3' como se fosse
             loja é o que quebra o recorte por loja (ver a migration).

          ⚠️ ELE TAMBÉM RECORTA O SELETOR DE EQUIPAMENTO, logo abaixo (31/08).

          ⚠️ SEM pré-seleção do CK: em branco = a loja, que é a maioria dos
             chamados. Mesmo princípio do tipo de solicitação, abaixo.
          ================================================================ */}
      {quiosques.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle} htmlFor="ch-quiosque">Para onde é o chamado</label>
          <select id="ch-quiosque" style={inputStyle} value={quiosque}
            onChange={(e) => {
              setQuiosque(e.target.value);
              // ⚠️ LIMPA OS CANAIS: o teto é derivado daqui (32 no restaurante,
              //    4 no quiosque). Manter o canal 9 ao trocar para um quiosque
              //    deixaria na tela uma escolha que o servidor recusa — e o erro
              //    só apareceria ao enviar, sem dizer o que mudou.
              setCanais([]);
            }}>
            {/* ⚠️ O `value` é o SUFIXO (`q.sufixo`), o texto é o APELIDO. Mandar o
                apelido no `value` gravaria 'MAL - CK L3' na coluna `QUIOSQUE` — e
                o servidor recusaria, listando as opções "certas" que a tela nem
                mostra. */}
            <option value="">{lojaAlvo || 'A loja'}</option>
            {quiosques.map((q) => (
              <option key={q.sufixo} value={q.sufixo}>{q.apelido}</option>
            ))}
          </select>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0 0' }}>
            Sua loja tem {quiosques.length === 1 ? 'um Dessert Center' : `${quiosques.length} Dessert Centers`} além
            do prédio principal. É por aqui que o técnico sabe <strong>onde ir</strong> — e é o que
            filtra os <strong>equipamentos</strong> logo abaixo.
          </p>
        </div>
      )}

      {/* ⚠️ SEM opção pré-selecionada, de propósito. O primeiro da lista é 82%
          dos casos e seria o default óbvio — mas o que já vem escolhido ninguém
          lê, e foi assim que a prioridade do Auvo virou 99,6% "Média". A ordem da
          lista já põe o mais comum em primeiro; escolher é um ato. */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle} htmlFor="ch-tipo">Tipo de solicitação</label>
        <select id="ch-tipo" style={inputStyle} value={tipoSolicitacao}
          onChange={(e) => {
            setTipoSolicitacao(e.target.value);
            // ⚠️ Trocar PARA a Solicitação de Imagem limpa o equipamento: o campo
            //    some da tela (o canal já diz o ponto), e um código escolhido
            //    antes seguiria no envio sem ninguém poder mais vê-lo ou tirá-lo
            //    — o chamado nasceria pendurado num aparelho que não é o assunto.
            //    Vale inclusive para quem chegou pelo QR do adesivo.
            if (ehSolicitacaoImagem(e.target.value)) setIdAtivo('');
            // ⚠️ O chip também não leva equipamento (DEM-00027): limpa o que
            //    tiver sido escolhido antes de trocar para o tipo de chip.
            if (ehChipTipo(e.target.value)) setIdAtivo('');
          }}>
          <option value="">Escolha…</option>
          {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {erroTipos && (
          <p style={{ fontSize: 12, color: '#b91c1c', margin: '6px 0 0 0' }}>
            ⚠️ {erroTipos} Sem os tipos não é possível abrir chamado — recarregue a página.
          </p>
        )}
      </div>

      {/* ==================================================================
          A SOLICITAÇÃO DE IMAGEM — canais, período, destino e motivo.

          ⚠️⚠️ O BLOCO INTEIRO APARECE E SOME COM O TIPO. Ele não é "mais uns
             campos opcionais": nos outros três tipos ele não existe, e o
             servidor RECUSA esses campos quando o tipo é outro.
          ⚠️ Mora AQUI, colado ao seletor de tipo, e não no fim do formulário:
             quem escolhe o tipo precisa ver na hora o que ele passou a pedir.
          ================================================================== */}
      {ehImagem && (
        <div style={{
          marginBottom: 20, padding: 16, borderRadius: 10,
          backgroundColor: COR_CLARA, border: `1px solid ${COR_A33}`,
        }}>
          <p style={{ margin: '0 0 14px 0', fontSize: 13, color: '#0f172a', lineHeight: 1.5 }}>
            <strong>Pedido de imagem das câmeras.</strong> Quanto mais exato o período,
            mais rápido o técnico acha a cena no gravador.
          </p>

          {/* OS CANAIS — caixas numeradas, não campo de texto: o número é o que o
              técnico procura no DVR, e clicar num número é mais rápido (e menos
              errável) do que digitar de pé na loja. */}
          <label style={labelStyle}>
            Câmeras — marque o número de cada uma
          </label>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6,
          }}>
            {Array.from({ length: tetoDeCanais(quiosque) }, (_, i) => i + 1).map((n) => {
              const marcada = canais.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  aria-pressed={marcada}
                  onClick={() => setCanais((atual) => (marcada
                    ? atual.filter((c) => c !== n)
                    : [...atual, n].sort((a2, b2) => a2 - b2)))}
                  style={{
                    minWidth: ehMobile ? 44 : 38, height: ehMobile ? 44 : 38,
                    borderRadius: 8, cursor: 'pointer',
                    fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    border: marcada ? `2px solid ${COR}` : '1px solid #cbd5e1',
                    backgroundColor: marcada ? COR : '#fff',
                    color: marcada ? '#fff' : '#475569',
                  }}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 18px 0' }}>
            {quiosque
              ? `Este quiosque tem ${tetoDeCanais(quiosque)} canais.`
              : `O restaurante tem até ${tetoDeCanais(quiosque)} canais.`}
            {canais.length > 0 && ` Marcadas: ${canais.length}.`}
            {' '}Não sabe o número? Descreva o ponto na descrição abaixo.
          </p>

          {/* O PERÍODO — dois `datetime-local`. A hora é o dado: é ela que o
              técnico usa para achar a cena. */}
          <div style={{
            display: 'grid', gap: 12, marginBottom: 18,
            gridTemplateColumns: ehMobile ? '1fr' : '1fr 1fr',
          }}>
            <div>
              <label style={labelStyle} htmlFor="ch-img-inicio">Início (data e hora)</label>
              <input
                id="ch-img-inicio" type="datetime-local" style={inputStyle}
                value={periodoInicio} onChange={(e) => setPeriodoInicio(e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="ch-img-fim">Fim (data e hora)</label>
              <input
                id="ch-img-fim" type="datetime-local" style={inputStyle}
                value={periodoFim} onChange={(e) => setPeriodoFim(e.target.value)}
              />
            </div>
          </div>

          {/* O DESTINO — lista fechada, sem pré-seleção (mesmo motivo do tipo). */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle} htmlFor="ch-img-destino">Destino da imagem</label>
            <select
              id="ch-img-destino" style={inputStyle}
              value={destinoImagem} onChange={(e) => setDestinoImagem(e.target.value)}
            >
              <option value="">Escolha…</option>
              {DESTINOS_IMAGEM.map((d) => (
                <option key={d.valor} value={d.valor}>{d.rotulo}</option>
              ))}
            </select>
          </div>

          {/* O MOTIVO — obrigatório por LGPD, e a tela DIZ isso. Campo obrigatório
              sem explicação vira campo preenchido com "solicitação". */}
          <div>
            <label style={labelStyle} htmlFor="ch-img-motivo">Motivo do pedido</label>
            <textarea
              id="ch-img-motivo" rows={2} style={{ ...inputStyle, resize: 'vertical' }}
              value={motivoImagem} onChange={(e) => setMotivoImagem(e.target.value)}
              placeholder="Ex.: conferir a troca de turno do dia 12 — divergência no fechamento do caixa."
            />
            <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0 0' }}>
              Imagem de câmera mostra <strong>pessoas</strong>. O motivo fica registrado
              no chamado — é ele que justifica o acesso se alguém perguntar depois.
            </p>
          </div>
        </div>
      )}

      {/* ==================================================================
          A SOLICITAÇÃO DE CHIP — colaborador, motivo, linha atual e observação.

          ⚠️⚠️ BLOCO ADMINISTRATIVO (DEM-00027): aparece só quando o tipo é chip
             (que o servidor só oferece ao Gerente titular). Dispensa foto e
             equipamento e não passa por triagem — vai direto para a Fernanda.
          ⚠️ A 'linha atual' só aparece nos motivos que partem de uma linha
             existente (troca/2ª via/cancelamento). Em 'chip novo' não há número.
          ================================================================== */}
      {ehChip && (
        <div style={{
          marginBottom: 20, padding: 16, borderRadius: 10,
          backgroundColor: COR_CLARA, border: `1px solid ${COR_A33}`,
        }}>
          <p style={{ margin: '0 0 14px 0', fontSize: 13, color: '#0f172a', lineHeight: 1.5 }}>
            <strong>Solicitação de chip celular.</strong> O pedido vai direto para o
            setor responsável — não passa por triagem nem precisa de foto.
          </p>

          {/* O COLABORADOR — para quem é o chip. */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle} htmlFor="ch-chip-colab">Colaborador (para quem é o chip)</label>
            <input
              id="ch-chip-colab" type="text" style={inputStyle}
              value={colaboradorChip} onChange={(e) => setColaboradorChip(e.target.value)}
              placeholder="Nome do colaborador"
              maxLength={150}
            />
          </div>

          {/* O MOTIVO — lista fechada, sem pré-seleção (mesmo motivo do tipo).
              ⚠️ Trocar o motivo para 'chip novo' limpa a linha atual: ela não
                 existe nesse caso e sobraria escondida no envio. */}
          <div style={{ marginBottom: motivoExigeLinha(motivoChip) ? 18 : 0 }}>
            <label style={labelStyle} htmlFor="ch-chip-motivo">Motivo</label>
            <select
              id="ch-chip-motivo" style={inputStyle}
              value={motivoChip}
              onChange={(e) => {
                setMotivoChip(e.target.value);
                if (!motivoExigeLinha(e.target.value)) setLinhaChip('');
              }}
            >
              <option value="">Escolha…</option>
              {MOTIVOS_CHIP.map((m) => (
                <option key={m.valor} value={m.valor}>{m.rotulo}</option>
              ))}
            </select>
          </div>

          {/* A LINHA ATUAL — só nos motivos que partem de uma linha existente. */}
          {motivoExigeLinha(motivoChip) && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle} htmlFor="ch-chip-linha">Linha/número atual</label>
              <input
                id="ch-chip-linha" type="text" style={inputStyle}
                value={linhaChip} onChange={(e) => setLinhaChip(e.target.value)}
                placeholder="Ex.: (27) 99999-0000"
                maxLength={30}
              />
            </div>
          )}

          {/* A OBSERVAÇÃO — opcional. */}
          <div>
            <label style={labelStyle} htmlFor="ch-chip-obs">Observação (opcional)</label>
            <textarea
              id="ch-chip-obs" rows={2} style={{ ...inputStyle, resize: 'vertical' }}
              value={obsChip} onChange={(e) => setObsChip(e.target.value)}
              placeholder="Algo que ajude o setor a atender o pedido."
              maxLength={500}
            />
          </div>
        </div>
      )}

      {/* ⚠️⚠️ O EQUIPAMENTO SOME NA SOLICITAÇÃO DE IMAGEM (Julia, 14/09/2026): o
          canal da câmera, marcado acima, JÁ diz de que ponto se trata — pedir o
          equipamento seria perguntar duas vezes a mesma coisa. Além disso o
          parque registra o CFTV como UM ativo (`Câmeras/DVR`) que nem toda loja
          tem cadastrado, e exigi-lo travaria o pedido por um cadastro que não é
          do pedido. O backend já trata o equipamento como opcional.
          ⚠️ Some também no CHIP (DEM-00027): pedido administrativo não tem
          equipamento. */}
      {!ehImagem && !ehChip && (
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle} htmlFor="ch-ativo-busca">Equipamento</label>

        {/* ⚠️⚠️ ERA UM `<select>` NATIVO com a loja inteira dentro (165 a 189
            opções, medido em 28/08). Rolar essa lista num tablet, com o
            equipamento quebrado na mão, é onde se desiste do portal. O campo de
            busca filtra conforme se digita, com a mesma normalização do GPS-TI
            (ver `chamados-busca-ativos.js` — é o que faz "nobreak" achar
            "No-break"). */}
        <input
          id="ch-ativo-busca"
          type="text"
          style={inputStyle}
          placeholder="Busque por nome, família ou código — ex.: impressora grill, KVS, POS-7K2P"
          value={buscaAtivo}
          onChange={(e) => setBuscaAtivo(e.target.value)}
          autoComplete="off"
        />

        {/* ⚠️ O AVISO DO RECORTE É OBRIGATÓRIO, não enfeite: quando a pessoa
            chega aqui vinda de uma ficha do guia, a lista está recortada por uma
            decisão que ELA NÃO TOMOU. Sem dizer isso na cara — e sem dar a saída
            no mesmo lugar —, o equipamento que falta parece não existir. */}
        {familiaFiltro && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
            margin: '8px 0 0 0', padding: '8px 12px', borderRadius: 8,
            backgroundColor: COR_CLARA, border: `1px solid ${COR_A22}`,
          }}>
            <span style={{ fontSize: 12, color: 'var(--ch-cor-escura)' }}>
              Mostrando só <strong>{familiaFiltro}</strong>, por causa da ficha que você abriu no guia.
            </span>
            <button
              type="button"
              onClick={() => setIgnorarFiltro(true)}
              style={{
                background: 'none', border: 'none', padding: 0, color: COR,
                fontWeight: 700, cursor: 'pointer', fontSize: 12, textDecoration: 'underline',
              }}
            >
              {/* ⚠️ O total é o do LUGAR escolhido, não o da loja: "ver todos os
                  189" num CK de 6 peças prometeria o que o clique não entrega. */}
              ver todos os {ativosDoLocal.length} equipamentos de {nomeDoLocal(quiosque)}
            </button>
          </div>
        )}

        {/* ⚠️ SELETOR FECHADO (dropdown), NÃO lista aberta. A primeira versão de
            28/08 usava `size={8}`, que deixa as opções à mostra o tempo todo —
            depois de escolher, a pessoa continuava vendo a lista inteira e não
            sabia dizer o que tinha selecionado (relatado pela Julia no mesmo
            dia). Fechado, o campo mostra exatamente uma coisa: a escolha. Quem
            recorta a lista é a busca acima. */}
        <select
          id="ch-ativo"
          style={{ ...inputStyle, marginTop: 8 }}
          value={idAtivo}
          onChange={(e) => setIdAtivo(e.target.value)}
        >
          {/* ⚠️⚠️ ERA "Não sei / não se aplica" — SAIU a pedido da Julia (28/08),
              e isso transformou o campo em OBRIGATÓRIO na tela. O placeholder é
              `disabled` para não poder ser reescolhido depois de sair dele.
              ⚠️ O BACKEND CONTINUA ACEITANDO CHAMADO SEM EQUIPAMENTO
              (`if (b.id_ativo)` no `POST /`), de propósito: a trava é de tela, e
              há chamado que legitimamente não tem uma peça — "a internet caiu",
              "a loja inteira sem produção" (o próprio guia tem 5 fichas assim).
              Se isso virar problema na operação, repor a opção vazia aqui é UMA
              linha, e nada mais precisa mudar. */}
          <option value="" disabled>Escolha o equipamento…</option>
          {/* ⚠️⚠️ A LISTA É FILTRADA PELO LUGAR DESDE 31/08/2026 (pedido da Julia),
              e isto REVERTE a decisão contrária que morava aqui. O comentário
              antigo dizia, com razão para a época, que filtrar esconderia o
              parque inteiro porque o `QUIOSQUE` era quase todo nulo — a coluna
              tinha nascido em 24/08. O que mudou é o DADO, não a opinião: a
              recarga da planilha oficial em 27/08 preencheu os 2.277 ativos (165
              de loja + 6 por CK, medido em 31/08), e o recorte passou a ser
              possível sem esconder nada.
              ⚠️ Ele é ESTRITO — ver `filtrarPorQuiosque`. O da FAMÍLIA (vindo do
              guia) falha aberto porque a pessoa não o pediu; este ela acabou de
              escolher no campo acima. Quem dá a saída é o aviso abaixo. */}
          {ativosVisiveis.map((a) => (
            <option key={a.CODIGO} value={a.CODIGO}>
              {a.FAMILIA} — {a.DESCRICAO} ({a.CODIGO}){a.QUIOSQUE ? ` · ${a.QUIOSQUE}` : ''}
            </option>
          ))}
        </select>
        {/* ⚠️ Três mensagens para três estados distintos. A primeira versão tinha
            UMA (nenhuma), e por isso "não carregou" era indistinguível de "a loja
            não tem equipamento cadastrado" — o sintoma que gerou a pergunta
            "por que não aparece?". */}
        {erroAtivos ? (
          <p style={{ fontSize: 12, color: '#b91c1c', margin: '6px 0 0 0' }}>
            ⚠️ {erroAtivos}{' '}
            <button type="button" onClick={carregarAtivos}
              style={{ background: 'none', border: 'none', padding: 0, color: COR, fontWeight: 700, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>
              tentar de novo
            </button>
          </p>
        ) : carregandoAtivos ? (
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0 0' }}>Carregando os equipamentos…</p>
        ) : !ativos.length ? (
          <p style={{ fontSize: 12, color: '#b45309', margin: '6px 0 0 0' }}>
            Nenhum equipamento cadastrado para a loja <strong>{lojaAlvo || '—'}</strong>.
            Você pode abrir o chamado sem escolher equipamento — e avise o TI para cadastrar o parque.
          </p>
        ) : !ativosDoLocal.length ? (
          /* ⚠️⚠️ ESTE ESTADO NASCEU COM O RECORTE POR LUGAR (31/08/2026), e ele é
             a razão de o `filtrarPorQuiosque` poder falhar FECHADO. A loja TEM
             parque, mas o lugar escolhido não tem nenhuma peça cadastrada —
             hoje não deveria acontecer (todo CK vigente veio com 6 na recarga de
             27/08), mas acontece no dia em que um CK novo abrir antes de o TI
             cadastrar o parque dele.
             ⚠️ A saída é EXPLÍCITA e devolve à loja, em vez de a tela decidir
             sozinha mostrar tudo: quem escolheu o lugar foi a pessoa. */
          <p style={{ fontSize: 12, color: '#b45309', margin: '6px 0 0 0' }}>
            Nenhum equipamento cadastrado em <strong>{nomeDoLocal(quiosque)}</strong>.{' '}
            <button type="button" onClick={() => setQuiosque('')}
              style={{ background: 'none', border: 'none', padding: 0, color: COR, fontWeight: 700, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>
              ver os {ativos.length} da loja
            </button>
          </p>
        ) : !ativosVisiveis.length ? (
          /* ⚠️ Só acontece com BUSCA digitada: o recorte por família nunca
             esvazia (ver `filtrarPorFamilia`) e o do lugar tem estado próprio,
             logo acima. Aqui a lista vazia é honesta — a pessoa procurou algo, e
             a tela diz o que fazer. */
          <p style={{ fontSize: 12, color: '#b45309', margin: '6px 0 0 0' }}>
            Nada encontrado para <strong>{buscaAtivo}</strong>{quiosque ? <> em <strong>{nomeDoLocal(quiosque)}</strong></> : null}.{' '}
            <button type="button" onClick={() => setBuscaAtivo('')}
              style={{ background: 'none', border: 'none', padding: 0, color: COR, fontWeight: 700, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>
              limpar a busca
            </button>
          </p>
        ) : (
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0 0' }}>
            {/* ⚠️ Mostra o VISÍVEL e o total quando eles diferem: dizer só "12
                equipamentos" com 165 na loja faria o recorte passar por
                inventário — e o gerente concluiria que falta cadastro. */}
            {/* ⚠️ O conselho "se não souber, deixe em branco" SAIU em 28/08 junto
                com a opção vazia — ele ensinava um caminho que não existe mais. */}
            {/* ⚠️⚠️ O DENOMINADOR É O DO LUGAR (`ativosDoLocal`), não o da loja.
                Dizer "6 de 189" para quem está olhando um CG de 6 peças faria a
                pessoa procurar as outras 183 que não estão lá — o recorte por
                lugar é anunciado no nome, logo abaixo. */}
            {ativosVisiveis.length === ativosDoLocal.length
              ? `${ativosDoLocal.length} equipamento(s) em ${nomeDoLocal(quiosque)}.`
              : `${ativosVisiveis.length} de ${ativosDoLocal.length} equipamento(s) em ${nomeDoLocal(quiosque)}.`}
            {' '}Use a busca acima se a lista estiver longa.
          </p>
        )}
      </div>
      )}

      {/* ⚠️⚠️ ASSUNTO E DESCRIÇÃO SOMEM NA SOLICITAÇÃO DE IMAGEM (Julia,
          14/09/2026), e quem os escreve é o SERVIDOR (`resumoDoPedido`): o pedido
          já está todo em campos estruturados acima e o "motivo" já é o texto
          livre — pedir os dois por cima disso é pedir a mesma coisa três vezes, e
          o que se digita por obrigação vira "solicitação de imagem" repetido.
          ⚠️ O assunto vira `Solicitação de Imagem — LOJA — data do período`. A
          data é a DO PERÍODO PEDIDO e não a de hoje: a de abertura já aparece em
          toda listagem; o que distingue um pedido do outro é de que dia é a
          imagem.
          ⚠️ Somem também no CHIP (DEM-00027): o assunto e a descrição são gerados
          pelo servidor a partir dos campos do pedido. */}
      {!ehImagem && !ehChip && (
      <>
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle} htmlFor="ch-titulo">Assunto</label>
        <input
          id="ch-titulo" style={inputStyle} value={titulo} maxLength={150}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ex.: POS do balcão 2 não imprime"
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle} htmlFor="ch-desc">O que está acontecendo</label>
        <textarea
          id="ch-desc" style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
          value={descricao} onChange={(e) => setDescricao(e.target.value)}
          placeholder="Desde quando acontece, o que já foi tentado e o que isso está impedindo na loja."
        />
        {/* O impacto é o que o TI usa para priorizar — e como a loja não escolhe
            a prioridade, é aqui que ela precisa aparecer. */}
        <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0 0' }}>
          Conte o <strong>impacto</strong> (ex.: "o drive está sem vender"). É por
          ele que o TI define a urgência.
        </p>
      </div>
      </>
      )}

      {/* ⚠️⚠️ A SEÇÃO DE FOTOS SOME NA SOLICITAÇÃO DE IMAGEM, e não é enfeite:
          não há o que fotografar num pedido de gravação, e a foto é obrigatória
          nos demais tipos. Deixá-la visível (mesmo "opcional") faria a loja
          procurar o que anexar. O servidor dispensa pela mesma regra.
          ⚠️ Some também no CHIP (DEM-00027): pedido administrativo não tem foto. */}
      {!ehImagem && !ehChip && (
      <div style={{ marginBottom: 28 }}>
        {/* ⚠️ O rótulo diz o que é OBRIGATÓRIO e o que é opcional, separadamente.
            "Fotos (até 5)" faria a obrigatoriedade só aparecer no erro ao enviar. */}
        <label style={labelStyle}>
          Fotos — 1 obrigatória, até {MAX_FOTOS - MIN_FOTOS} extras
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {fotos.map((f, i) => (
            <div key={f.url} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
              backgroundColor: COR_CLARA, borderRadius: 8, fontSize: 12, color: '#0f172a', maxWidth: 240,
            }}>
              <ImageIcon size={14} color={COR} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.nome}</span>
              <button
                type="button" aria-label={`Remover ${f.nome}`}
                onClick={() => setFotos((atual) => atual.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', padding: 0 }}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {/* ⚠️ Dois gatilhos (EscolherAnexo): "Tirar foto" devolve a CÂMERA ao
              Android, que num <input> só ia direto para a fototeca. A galeria
              aceita VÁRIAS de uma vez (`multiplo`), com o teto no `anexarFotos`.
              A obrigatoriedade da 1ª foto segue no texto abaixo (não mais na cor
              do botão). Foto-only: `accept="image/*"`. */}
          {fotos.length < MAX_FOTOS && (
            <EscolherAnexo
              accept="image/*"
              multiplo
              cor={COR}
              ocupado={subindo}
              rotulo={fotos.length < MIN_FOTOS ? 'Escolher a foto do problema' : 'Escolher da galeria'}
              onArquivo={subirFoto}
              onArquivos={anexarFotos}
            />
          )}
        </div>
        {fotos.length < MIN_FOTOS && (
          <p style={{ fontSize: 12, color: '#b45309', margin: '8px 0 0 0' }}>
            Uma foto do problema é obrigatória. Serve a tela do erro, o cabo solto,
            o equipamento apagado — o que o técnico veria se estivesse aí.
          </p>
        )}
      </div>
      )}

      {/* ==================================================================
          A DATA DE ABERTURA — ⚠️⚠️ PROVISÓRIO, SOME SOZINHO DEPOIS DE 18/09/2026.

          POR QUE ELE EXISTE (Julia, 10/09/2026): o módulo abre para a rede
          inteira nesta tarde, e cada loja chega com chamados que JÁ ESTAVAM
          abertos no Auvo — alguns de semanas atrás. Transcrevê-los sem este
          campo os faria nascer todos com a data de hoje, e o portal passaria a
          mentir sobre há quanto tempo a loja espera: exatamente o número que
          ele existe para consertar.

          ⚠️⚠️ FICA NO FIM, JUNTO DO BOTÃO, e a posição é decisão. No topo ele
             seria a PRIMEIRA pergunta de todo mundo — inclusive dos 95% que
             estão abrindo um chamado normal e não têm data nenhuma a informar.
             Aqui ele é a última coisa antes de enviar, na caixa âmbar que já
             se lê como "aviso, não campo obrigatório".

          ⚠️ EM BRANCO É O CASO NORMAL. Não há asterisco, não há erro ao deixar
             vazio, e o texto diz isso com todas as letras — senão a loja
             preenche a data de hoje "por garantia" e carimba de migração um
             chamado que nasceu agora.

          ⚠️ `min`/`max` no input são a MESMA régua do servidor (o espelho): o
             seletor nativo já impede escolher amanhã ou o ano errado, e quem
             burlar leva 400 com a mesma mensagem.

          ⚠️ NÃO ENTRA NO RASCUNHO (`snapshotRascunho`), de propósito: rascunho
             sobrevive dias, e uma data de migração restaurada depois da janela
             seria recusada num formulário que parece certo.

          ⚠️⚠️ SÓ APARECE PARA `Gerente TI` E `Admin` (`podeInformarAbertura`,
             espelhada no backend). A loja abre chamado novo; retroagir data é
             do TI, que tem a fila do Auvo na mão. Ver `mostrarCampoAbertura`.

          COMO DESLIGAR: nada a fazer — passou 18/09, `janelaMigracaoAberta()`
          devolve falso e este bloco inteiro deixa de ser renderizado. Só então
          vale apagar o campo, o import e o `chamados-migracao-auvo.js`.
          ================================================================== */}
      {mostrarCampoAbertura && (
        <div style={{
          marginBottom: 20, padding: 14, borderRadius: 10,
          backgroundColor: '#fffbeb', border: '1px solid #fde68a',
        }}>
          <p style={{ margin: '0 0 4px 0', fontSize: 13, fontWeight: 800, color: '#78350f' }}>
            Este chamado já estava aberto no Auvo?
          </p>
          <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
            Só neste caso informe <strong>a data em que ele foi aberto lá</strong> — é o que
            mantém o tempo de espera real da sua loja. Chamado novo:{' '}
            <strong>deixe em branco</strong>, ele nasce com a data de hoje.
          </p>
          <label style={{ ...labelStyle, color: '#78350f' }} htmlFor="ch-aberto-em">
            Data de abertura no Auvo (opcional)
          </label>
          <input
            id="ch-aberto-em"
            type="date"
            value={dataAbertura}
            min={INICIO_PERMITIDO}
            max={hojeISO}
            onChange={(e) => setDataAbertura(e.target.value)}
            style={{
              ...inputStyle,
              maxWidth: ehMobile ? '100%' : 220,
              borderColor: dataAbertura ? '#d97706' : '#cbd5e1',
              // ⚠️ `tabular-nums` porque é número em campo estreito — mesma
              //    regra do resto do portal.
              fontVariantNumeric: 'tabular-nums',
            }}
          />
          {dataAbertura && (
            <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
              Será registrado como aberto em <strong>{formatarBR(dataAbertura)}</strong>, marcado
              como <strong>migrado do Auvo</strong> — e o prazo conta a partir dessa data, então
              ele pode já entrar atrasado. É o correto: a loja espera desde lá.{' '}
              <button
                type="button"
                onClick={() => setDataAbertura('')}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: '#92400e', fontSize: 12, fontWeight: 700, textDecoration: 'underline',
                }}
              >
                Limpar
              </button>
            </p>
          )}
          <p style={{ margin: '8px 0 0 0', fontSize: 11, color: '#a16207' }}>
            Campo disponível até {formatarBR(FIM_JANELA)}, só para a migração do Auvo.
          </p>
        </div>
      )}

      {/* ==================================================================
          O QUE ACONTECE DEPOIS — a tabela de prazos do SLA (Julia, 09/09)

          ⚠️⚠️ FICA NO FIM DO FORMULÁRIO, JUNTO DO BOTÃO, e a posição é decisão:
             logo abaixo do seletor de TIPO (o lugar óbvio) ela sugeriria que o
             tipo escolhido determina o prazo — e não determina, quem determina é
             a PRIORIDADE, definida na triagem. Aqui ela responde a pergunta que
             a pessoa tem no momento de enviar: "e quando alguém vem?".

          ⚠️⚠️ DIZ QUE O PRAZO DELE AINDA NÃO EXISTE, e essa frase é obrigatória:
             a loja NÃO escolhe a prioridade (é a trava mais defendida do módulo —
             no Auvo, quem sofria o problema escolhia e deu 81% "Alta"). Mostrar
             três prazos sem dizer que a triagem decide qual vale deixaria a
             pessoa achar que pode contar com o de 18 horas.

          ⚠️ Some inteiro se a API não responder (`prazosSla` nulo): é apoio, não
             requisito para abrir chamado.
          ================================================================== */}
      {prazosSla && (
        <div style={{
          marginBottom: 20, padding: 14, borderRadius: 10,
          backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
        }}>
          <p style={{ margin: '0 0 4px 0', fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
            O que acontece depois que você enviar
          </p>
          <p style={{ margin: '0 0 10px 0', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
            O TI classifica a urgência do seu chamado (você não precisa escolher) e
            é ela que define o prazo. A contagem começa{' '}
            {/* ⚠️ TEMPORÁRIO ATÉ 18/09: com data de migração informada, dizer
                "agora" seria falso — o relógio do SLA corre do `ABERTO_EM`, que
                nesse caso é a data digitada. Ver `chamados-migracao-auvo.js`. */}
            <strong>{dataAbertura ? `em ${formatarBR(dataAbertura)}` : 'agora'}</strong>, na
            abertura — não na hora em que o técnico pega o chamado.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: 320 }}>
              <thead>
                <tr>
                  <th style={thPrazo}>Urgência</th>
                  <th style={thPrazo}>Alguém assume em</th>
                  <th style={thPrazo}>Resolvido em</th>
                </tr>
              </thead>
              <tbody>
                {prazosSla.faixas.map((f) => (
                  <tr key={f.prioridade}>
                    <td style={tdPrazo}>
                      {/* ⚠️ O rótulo com acento vem do servidor ('Media' → 'Média'):
                          é onde o de-para mora, para não haver um `=== 'Media'` a mais. */}
                      <strong>{f.rotulo}</strong>
                    </td>
                    <td style={tdPrazo}>{f.atendimento} {f.unidade}</td>
                    <td style={tdPrazo}>{f.solucao} {f.unidade}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
            Se depender de peça ou de outro fornecedor, o técnico registra o motivo e
            uma <strong>previsão</strong> — e o prazo fica pausado até resolver. Você
            acompanha tudo em <strong>Chamados</strong>.
          </p>
        </div>
      )}

      <button
        type="button" onClick={enviar} disabled={enviando || subindo}
        style={{
          // ⚠️ No celular o botão ocupa a linha inteira: é a ação final de um
          //    formulário longo, e alvo largo é o que evita errar o toque depois
          //    de rolar a tela toda.
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          width: ehMobile ? '100%' : 'auto',
          padding: ehMobile ? '14px 24px' : '12px 24px', borderRadius: 8, border: 'none',
          background: enviando || subindo ? '#cbd5e1' : `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
          color: '#fff', fontSize: 15, fontWeight: 700,
          cursor: enviando || subindo ? 'not-allowed' : 'pointer',
          boxShadow: enviando || subindo ? 'none' : '0 4px 6px rgba(107, 33, 168, 0.35)',
        }}
      >
        <Send size={18} /> {enviando ? 'Abrindo…' : 'Abrir chamado'}
      </button>

      <p style={{ fontSize: 12, color: '#94a3b8', margin: '14px 0 0 0', lineHeight: 1.5 }}>
        O Gerente de TI é avisado por e-mail e faz a triagem — define a prioridade
        e o técnico. Você acompanha tudo na aba <strong>Chamados</strong>.
      </p>
    </div>
  );
};

export default ChamadosAbertura;
